import React, { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { Link } from 'gatsby-plugin-intl';

import { MenuLink } from '@components/menuGroup';
import BurgerIcon from '../icons/burgerIcon.svg';

import * as styles from './navigation.module.css';
import classnames from 'classnames';

import BackArrow from '../icons/back_arrow.svg';

import RockerLogo from '../images/header/rocker-logo.svg';
import ukraineFlag from '../images/ukraine/flag-of-ukraine.svg';
import HamburgerIcon from '../icons/hamburger.svg';

import CancelIcon from '../icons/cancel.svg';
import Chevron from '@components/icons/chevron';
import Arrow from '@components/icons/arrow';
import { AppBadges } from './HomeHeader';
import { useResizeDetector } from 'react-resize-detector';
import { AppDownloadUrl } from '../pageConstants';

const ChevronDown = props => <Chevron orientation="down" {...props} />;
const ChevronRight = props => <Chevron orientation="right" {...props} />;
const ArrowRight = props => <Arrow orientation="right" {...props} />;

const UkraineFlag = () => (
  <img
    style={{ display: 'inline-block', margin: '0 8px' }}
    src={ukraineFlag}
    alt="Ukraine flag"
  />
);

const MobileNavbar = ({ toggleMobileNavbar, firstLinkSection, logo, path }) => {
  const menuContainerStyle = classnames({});

  const addedNonProductCategory = [
    ...firstLinkSection.links.list,
    { path: 'about-us', name: 'About Rocker' },
  ];
  const background = [
    '/index',
    '/hjalp',
    '/prisgaranti',
    '/vart-ansvar',
  ].includes(path)
    ? '#f0f8fa'
    : '#fff';
  return (
    <>
      <div className={menuContainerStyle}>
        <div>
          <div onClick={toggleMobileNavbar}>
            <div>
              <div>Hem</div>
              <div>
                <BackArrow />
              </div>
            </div>
          </div>
          <div>
            <div>
              {addedNonProductCategory.map(link => {
                return (
                  <MenuLink
                    key={link.path}
                    path={link.path}
                    name={link.name}
                  ></MenuLink>
                );
              })}
            </div>
          </div>
          <div>
            <p>Har du en pågående ansökan?</p>
            <Link to="/fortsatt">Återuppta ansökan</Link>
          </div>
        </div>
      </div>

      <div style={{ background }}>
        <div>
          <div onClick={() => toggleMobileNavbar()}>
            <BurgerIcon />
          </div>
          <Link to="/">
            <img src={logo.file.url} alt="Rocker"></img>
          </Link>
        </div>
      </div>
    </>
  );
};

MobileNavbar.propTypes = {
  toggleMobileNavbar: propTypes.func,
  firstLinkSection: propTypes.object,
  logo: propTypes.object,
  path: propTypes.string,
};

const DesktopNavbar = ({ logo, path }) => {
  const containerClass = classnames({});
  const background = [
    '/index',
    '/spara',
    '/prisgaranti',
    '/vart-ansvar',
  ].includes(path)
    ? '#f0f8fa'
    : '#fff';
  return (
    <div
      style={{
        display: 'flex',
        flex: 1,
        background,
        justifyContent: 'center',
      }}
    >
      <div className={containerClass}>
        <Link to="/">
          <img src={logo.file.url}></img>
        </Link>

        <div>
          <div>
            <Link to="/kort">KORT</Link>
          </div>

          <div>
            <Link to="/kredit">KONTOKREDIT</Link>
          </div>

          <div>
            <div>
              <Link to="/lan">LÅN</Link>
            </div>

            <div>
              <Link to="/spara">SPARA</Link>
            </div>

            <div>
              <Link to="/checkout">ROCKER PAY</Link>
            </div>
            <div>
              <Link to="/about-us">ABOUT ROCKER</Link>
            </div>
            <div>
              <Link to="/hjalp">FAQ</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

DesktopNavbar.propTypes = {
  logo: propTypes.object,
  path: propTypes.string,
};

const showToast = false;

const Toast = React.forwardRef((props, ref) => (
  <div className={styles.toast} ref={ref}>
    <p>
      <UkraineFlag />
      Vi på Rocker gör allt vi kan för att hjälpa behövande i Ukraina.&nbsp;
      <Link to="/ukraine">Se vad vi gör och hur du kan hjälpa till här.</Link>
      <UkraineFlag />
    </p>
  </div>
));

Toast.displayName = 'Toast';

const Navigation = ({
  logo,
  customLinks,
  hideDownloadLinks,
  newStyle,
  nonOverlayed,
  opaqueBg,
  lightBg,
  homeUrl,
  heightCallback,
  visibleCallback,
}) => {
  const [previousScrollY, setPreviousScrollY] = useState(0);
  const [stickyY, setStickyY] = useState(0);

  const [isVisible, setIsVisible] = useState(true);

  const { ref: navElement, height: navHeight } = useResizeDetector();
  const { ref: toastElement, height: toastHeight } = useResizeDetector();

  useEffect(() => {
    heightCallback?.(toastElement.current?.offsetHeight || toastHeight);
  }, [toastHeight]);

  useEffect(() => {
    visibleCallback?.(isVisible);
  }, [isVisible]);

  const [hamburgerMenuVisible, setHamburgerMenuVisible] = useState(false);
  const [nestedMenuVisible, setNestedMenuVisible] = useState(false);
  const [activeLinkGroup, setActiveLinkGroup] = useState(null);

  const toggleNestedMenu = e => {
    e.preventDefault();
    e.stopPropagation();
    setNestedMenuVisible(!nestedMenuVisible);
  };

  const toggleLinkGroup = (e, groupID) => {
    e.preventDefault();
    e.stopPropagation();
    if (groupID !== null && groupID === activeLinkGroup) {
      setActiveLinkGroup(null);
    } else {
      setActiveLinkGroup(groupID);
    }
  };

  useEffect(() => {
    if (!hamburgerMenuVisible) {
      setNestedMenuVisible(false);
    }
  }, [hamburgerMenuVisible]);

  useEffect(() => {
    if (!nestedMenuVisible) {
      setActiveLinkGroup(null);
    }
  }, [nestedMenuVisible]);

  const logoHref = homeUrl || '/';

  const {
    allContentfulNavigation: {
      nodes: [{ groups, links }],
    },
  } = useStaticQuery(graphql`
    query NavigationQuery {
      allContentfulNavigation(
        filter: { name: { eq: "New Rocker Navbar" }, node_locale: { eq: "sv" } }
      ) {
        nodes {
          links {
            name
            url
            mobileOnly
            navGroup {
              name
              contentfulid
            }
          }
          groups {
            name
            contentfulid
          }
        }
      }
    }
  `);

  const directLinks = links.filter(link => link.navGroup === null);

  // TODO load from contentful?
  const dropdownMenuTitle = 'Varför Rocker?';
  const groupLevelLink = '/';
  const groupLevelLinkTitle = 'Full översikt';

  useEffect(() => {
    document.body.style.position = '';
    return () => {
      document.body.style.position = '';
    };
  }, []);

  useEffect(() => {
    const onScroll = e => {
      const newScrollY = e.target.documentElement.scrollTop;

      const dy = previousScrollY - newScrollY;
      const nav = navElement.current;
      const nHeight = nav?.offsetHeight || navHeight || 0;
      const tHeight = toastElement?.current?.offsetHeight || toastHeight || 0;
      const fixedContentHeight = nHeight + tHeight;

      let newY = stickyY + dy;
      if (newScrollY >= fixedContentHeight) {
        if (dy < 0) {
          newY = -fixedContentHeight;
          setIsVisible(false);
          setPreviousScrollY(Math.max(newScrollY, 0));
        } else if (dy > 0) {
          newY = 0;
          setIsVisible(true);
          setPreviousScrollY(Math.max(newScrollY, 0));
        }
      } else {
        setIsVisible(true);
        setPreviousScrollY(newScrollY);
        newY = Math.min(0, Math.max(-fixedContentHeight, newY));
      }

      setStickyY(newY);
    };

    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, [setPreviousScrollY, previousScrollY, stickyY, setStickyY]);

  const downloadLink = AppDownloadUrl;

  const headerStyles = [styles.newHeader];
  if (newStyle && previousScrollY < navHeight) {
    headerStyles.push(styles.transparentHeader);
  }

  if (hamburgerMenuVisible) {
    headerStyles.push(styles.menuVisible);
  }

  const hideable = previousScrollY > 4;

  const navTop = Math.max(-previousScrollY, stickyY);

  return (
    <div
      className={classnames({
        [styles.newHeaderWrapper]: true,
        [styles.overlayed]: newStyle && !nonOverlayed,
        [styles.opaqueBg]: opaqueBg,
        [styles.lightBg]: lightBg,
        [styles.hideable]: hideable,
      })}
      style={{
        top: hideable ? navTop : undefined,
      }}
    >
      {showToast && <Toast ref={toastElement} />}
      <nav className={headerStyles.join(' ')} ref={navElement}>
        <div className={styles.hamburgerAndLogo}>
          <button
            className={styles.hamburgerButton}
            onClick={() => setHamburgerMenuVisible(true)}
            aria-label="Öppna Meny"
          >
            <HamburgerIcon />
          </button>
          <Link to={logoHref}>
            <img
              className={styles.brand}
              alt="Rocker Logo"
              src={logo || RockerLogo}
            />
          </Link>
        </div>
        <div className={styles.links}>
          <ul style={{ display: 'flex', gap: '2rem', listStyle: 'none' }}>
            {links.map(link => (
              <li key={link.url}>
                <Link to={link.url}>{link.name}</Link>
              </li>
            ))}
          </ul>
        </div>
        {!hideDownloadLinks && (
          <a
            href={downloadLink}
            target="_blank"
            rel="noreferrer noopener"
            className={styles.appDownloadButton}
          >
            Ladda ner
          </a>
        )}
        <div className={styles.mobileMenu}>
          <div className={styles.mobileMenuHeader}>
            <button
              onClick={() => setHamburgerMenuVisible(false)}
              aria-label="Stäng meny"
            >
              <CancelIcon />
            </button>
            <Link to={logoHref}>
              <img
                className={styles.brand}
                alt="Rocker Logo"
                src={logo || RockerLogo}
              />
            </Link>
          </div>
          <nav className={styles.mobileNavigation}>
            <ul className={styles.mobileLinks}>
              {links.map(link => (
                <li key={link.url}>
                  <Link to={link.url}>{link.name}</Link>
                </li>
              ))}
            </ul>
            {!hideDownloadLinks && (
              <div className={styles.appBadgesWrapper}>
                <AppBadges bordered={true} />
              </div>
            )}
          </nav>
        </div>
      </nav>
    </div>
  );
};

Navigation.propTypes = {
  path: propTypes.string,
  logo: propTypes.string,
  linkContent: propTypes.array,
  customLinks: propTypes.array,
  hideDownloadLinks: propTypes.bool,
  newStyle: propTypes.bool,
  opaqueBg: propTypes.bool,
  lightBg: propTypes.bool,
  nonOverlayed: propTypes.bool,
  homeUrl: propTypes.string,
  heightCallback: propTypes.func,
  visibleCallback: propTypes.func,
};

Navigation.defaultProps = {};

export default Navigation;
